import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createTheme, Drawer, Grid, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ChevronDown, ChevronUp, AlertTriangle, BellRing } from "lucide-react";
import "./sidebar.css";
import logo from "../../assets/images/logo.png";
import SidebarItem from "./SidebarItem";
import {
  LayoutDashboard,
  Package,
  Users,
  Settings,
  Tags,
  Container,
  ShoppingBag,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { isAuth, profilePicture } from "../../actions/auth";
import { Image_API } from "../../config";
import { Boxes } from "lucide-react";
import { Shield } from "lucide-react";
import { UsersRound } from "lucide-react";
import {
  BarChart2,
  FileText,
  Archive,
  CreditCard,
  Truck,
  Activity,
} from "lucide-react";
import { ShoppingCart } from "lucide-react";
import { Plane } from "lucide-react";
import Subscription from "../../actions/Subscription";
import { checkReportStatuses } from "../../utils/utils";

const drawerWidth = 270;
const collapsedDrawerWidth = 60;

const admin_routes = [
  {
    display_name: "Dashboard",
    route: "/",
    icon: <LayoutDashboard />,
  },
  {
    display_name: "Inventories",
    icon: <Boxes />,
    subRoutes: [
      {
        display_name: "Inventories",
        route: "/products",
        icon: <Package />,
      },
      {
        display_name: "Categories",
        route: "/categories",
        icon: <Tags />,
      },
      {
        display_name: "Containers",
        route: "/containers",
        icon: <Container />,
      },
    ],
  },
  {
    display_name: "Customers",
    route: "/customers",
    icon: <UserPlus />,
  },
  {
    display_name: "General",
    icon: <Settings />,
    subRoutes: [
      {
        display_name: "Settings",
        route: "/settings",
        icon: <Settings />,
      },
      {
        display_name: "Subscriptions",
        route: "/subscriptions",
        icon: <BellRing />,
      },
    ],
  },
  {
    display_name: "Reports",
    icon: <AlertTriangle />,
    subRoutes: [
      {
        display_name: "Sales Report",
        route: "/sales-reports",
        icon: <BarChart2 />,
      },
      {
        display_name: "Sales Detail Report",
        route: "/detail-reports",
        icon: <FileText />,
      },
      {
        display_name: "Inventory Report",
        route: "/inventory",
        icon: <Archive />,
      },
      {
        display_name: "Inventory Report By Container",
        route: "/container-report",
        icon: <Container />,
      },
      {
        display_name: "Activity Log Report",
        route: "/activity-log",
        icon: <Activity />,
      },
      {
        display_name: "Payment Report",
        route: "/payment-report",
        icon: <CreditCard />,
      },
      {
        display_name: "Purchase Order Report",
        route: "/purchase-container",
        icon: <Truck />,
      },
    ],
  },
];

const superadmin_routes = [
  {
    display_name: "Dashboard",
    route: "/admin",
    icon: <LayoutDashboard />,
  },
  {
    display_name: "Organization",
    route: "/admin/organization",
    icon: <Settings />,
  },
  {
    display_name: "Organization admin",
    route: "/admin/staff",
    icon: <Users />,
  },
  {
    display_name: "Register",
    route: "/admin/signup",
    icon: <UserPlus />,
  },
  {
    display_name: "Subscriptions",
    route: "/admin/subscriptions",
    icon: <BellRing />,
  },
];
const theme = createTheme();

const useStyles = makeStyles(() => ({
  drawer: {
    width: (props) => (props.collapsed ? collapsedDrawerWidth : drawerWidth),
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaper: {
    width: (props) => (props.collapsed ? collapsedDrawerWidth : drawerWidth),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  borderR: {
    borderRight: "none",
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

const Sidebar = (props) => {
  const history = useHistory();
  const classes = useStyles({ collapsed: props.collapsed });
  const location = useLocation();
  const [open, setOpen] = useState({});
  const [routes, setRoutes] = useState([]);
  const [service, setService] = useState([]);

  const { viewOrgSubscription } = Subscription();
  useEffect(() => {
    if (isAuth()?.staffOrganizations) {
      viewOrgSubscription(
        isAuth()?.staffOrganizations[0]?.organization?.id
      ).then((data) => {
        if (data.subscription) {
          setService(data.subscription.subscription.subscriptionServices);
        }
      });
    }
  }, []);

  useEffect(() => {
    const authData = isAuth();

    let newAdminRoutes = [...admin_routes];
    let reportStatus = {
      "Activity Report": false,
      "Sales Report": false,
      "Payment Report": false,
      "Inventory Report": false,
      "Purchase Order Report": false,
    };

    if (service.length > 0) {
      reportStatus = checkReportStatuses(service);
    }

    const allPermissions = authData?.staffRoles?.flatMap(
      (staffRole) => staffRole.role?.rolePermissions || []
    );

    const hasManageStaffPermission = allPermissions?.some(
      (permission) => permission.action === "manageStaff"
    );
    const hasPoPermission = allPermissions?.some(
      (permission) => permission.action === "viewPo"
    );
    const hasGeneralSettingPermission = allPermissions?.some(
      (permission) => permission.action === "generalSetting"
    );
    const hasStoreKeeperPermission = allPermissions?.some(
      (permission) =>
        permission.action === "onlyMineShipment" ||
        permission.action === "viewAllShipment"
    );
    const hasSalesPermission = allPermissions?.some(
      (permission) =>
        permission.action === "addOrder" || permission.action === "viewOrders"
    );
    const hasSalesReportPermission = allPermissions?.some(
      (permission) => permission.action === "salesReport"
    );
    const hasActivityReportPermission = allPermissions?.some(
      (permission) => permission.action === "activityReport"
    );
    const hasInventoryReportPermission = allPermissions?.some(
      (permission) => permission.action === "inventoryReport"
    );
    const hasContainerReportPermission = allPermissions?.some(
      (permission) => permission.action === "inventoryReport"
    );

    if (hasPoPermission || authData.role === "admin") {
      const purchaseOrderIndex = newAdminRoutes.findIndex(
        (route) => route.display_name === "Purchase Orders"
      );
      if (purchaseOrderIndex === -1) {
        newAdminRoutes.splice(1, 0, {
          display_name: "Purchase Orders",
          route: "/purchase-order",
          icon: <ShoppingBag />,
        });
      }
    }

    if (hasManageStaffPermission || authData.role === "admin") {
      const staffsIndex = newAdminRoutes.findIndex(
        (route) => route.display_name === "Staffs"
      );
      if (staffsIndex === -1) {
        newAdminRoutes.splice(3, 0, {
          display_name: "Staffs",
          icon: <Users />,
          subRoutes: [
            {
              display_name: "Roles",
              route: "/roles",
              icon: <Shield />,
            },
            {
              display_name: "Staffs",
              route: "/staffs",
              icon: <UsersRound />,
            },
          ],
        });
      }
    }

    const generalIndex = newAdminRoutes.findIndex(
      (route) => route.display_name === "General"
    );
    if (generalIndex !== -1) {
      const generalSubRoutes = [];
      if (hasGeneralSettingPermission || authData.role === "admin") {
        generalSubRoutes.push({
          display_name: "Settings",
          route: "/settings",
          icon: <Settings />,
        });
      }
      if (hasManageStaffPermission || authData.role === "admin") {
        generalSubRoutes.push({
          display_name: "Subscriptions",
          route: "/subscriptions",
          icon: <BellRing />,
        });
      }
      if (generalSubRoutes.length > 0) {
        newAdminRoutes[generalIndex].subRoutes = generalSubRoutes;
      } else {
        newAdminRoutes.splice(generalIndex, 1);
      }
    }

    if (hasStoreKeeperPermission || authData.role === "admin") {
      const shipmentIndex = newAdminRoutes.findIndex(
        (route) => route.display_name === "Shipment"
      );
      if (shipmentIndex === -1) {
        newAdminRoutes.splice(5, 0, {
          display_name: "Shipment",
          route: "/shipment-order",
          icon: <Plane />,
        });
      }
    }

    if (hasSalesPermission || authData.role === "admin") {
      const salesIndex = newAdminRoutes.findIndex(
        (route) => route.display_name === "Sales Order"
      );
      if (salesIndex === -1) {
        newAdminRoutes.splice(4, 0, {
          display_name: "Sales Order",
          route: "/orders",
          icon: <ShoppingCart />,
        });
      }
    }

    const reportsIndex = newAdminRoutes.findIndex(
      (route) => route.display_name === "Reports"
    );
    if (reportsIndex !== -1) {
      const reportSubRoutes = [];
      if (
        (hasSalesReportPermission || authData.role === "admin") &&
        reportStatus["Sales Report"]
      ) {
        reportSubRoutes.push(
          {
            display_name: "Sales Report",
            route: "/sales-reports",
            icon: <BarChart2 />,
          },
          {
            display_name: "Sales Detail Report",
            route: "/detail-reports",
            icon: <FileText />,
          }
        );
      }
      if (
        (hasInventoryReportPermission || authData.role === "admin") &&
        reportStatus["Inventory Report"]
      ) {
        reportSubRoutes.push({
          display_name: "Inventory Report",
          route: "/inventory",
          icon: <Archive />,
        });
      }
      if (
        (hasContainerReportPermission || authData.role === "admin") &&
        reportStatus["Inventory Report"]
      ) {
        reportSubRoutes.push({
          display_name: "Inventory Report By Container",
          route: "/container-report",
          icon: <Container />,
        });
      }
      if (
        (hasContainerReportPermission || authData.role === "admin") &&
        reportStatus["Payment Report"]
      ) {
        reportSubRoutes.push({
          display_name: "Payment Report",
          route: "/payment-report",
          icon: <CreditCard />,
        });
      }
      if (
        (hasContainerReportPermission || authData.role === "admin") &&
        reportStatus["Purchase Order Report"]
      ) {
        reportSubRoutes.push({
          display_name: "Purchase Order Report",
          route: "/purchase-container",
          icon: <Truck />,
        });
      }
      if (
        (hasActivityReportPermission || authData.role === "admin") &&
        reportStatus["Activity Report"]
      ) {
        reportSubRoutes.push({
          display_name: "Activity Log Report",
          route: "/activity-log",
          icon: <Activity />,
        });
      }

      if (reportSubRoutes.length > 0) {
        newAdminRoutes[reportsIndex].subRoutes = reportSubRoutes;
      } else {
        newAdminRoutes.splice(reportsIndex, 1);
      }
    }

    const finalReportsIndex = newAdminRoutes.findIndex(
      (route) => route.display_name === "Reports"
    );
    if (finalReportsIndex !== -1) {
      const [reportsItem] = newAdminRoutes.splice(finalReportsIndex, 1);
      newAdminRoutes.push(reportsItem);
    }

    const finalGeneralIndex = newAdminRoutes.findIndex(
      (route) => route.display_name === "General"
    );
    if (finalGeneralIndex !== -1) {
      const [generalItem] = newAdminRoutes.splice(finalGeneralIndex, 1);
      newAdminRoutes.push(generalItem);
    }

    if (location.pathname.includes("/admin")) {
      newAdminRoutes = [...superadmin_routes];
    }

    const order = [
      "Dashboard",
      "Purchase Orders",
      "Inventories",
      "Sales Order",
      "Customers",
      "Shipment",
      "Staffs",
      "General",
      "Reports",
    ];

    if (location.pathname.includes("/admin")) {
      setRoutes(newAdminRoutes);
    } else {
      const orderedRoutes = order
        .map((routeName) => {
          return newAdminRoutes.find(
            (route) => route.display_name === routeName
          );
        })
        .filter(Boolean);
      setRoutes(orderedRoutes);
    }
  }, [location.pathname, service]);

  useEffect(() => {
    const currentPath = location.pathname;
    routes.forEach((item, index) => {
      if (item.subRoutes) {
        item.subRoutes.forEach((subItem) => {
          if (currentPath.includes(subItem.route)) {
            setOpen((prevOpen) => ({ ...prevOpen, [index]: true }));
          }
        });
      }
    });
  }, [location.pathname, routes]);

  const handleToggle = (index) => {
    if (!props.collapsed) {
      setOpen((prevOpen) => ({ ...prevOpen, [index]: !prevOpen[index] }));
    }
  };

  const handleMenuItemClick = (route) => {
    if (window.innerWidth <= 768) {
      props.handleClose();
    }
    history.push(route);
  };

  const activeItem = routes.findIndex(
    (item) => item.route === location.pathname
  );

  const renderMenuItem = (item, index) => {
    if (item.subRoutes) {
      if (props.collapsed) {
        const tooltipContent = (
          <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              {item.display_name}
            </div>
            {item.subRoutes.map((subItem, subIndex) => (
              <div
                key={subIndex}
                style={{ padding: "4px 0", cursor: "pointer" }}
                onClick={() => handleMenuItemClick(subItem.route)}
              >
                {subItem.display_name}
              </div>
            ))}
          </div>
        );

        return (
          <Tooltip
            key={index}
            title={tooltipContent}
            placement="right"
            interactive
            arrow
          >
            <div>
              <SidebarItem
                title=""
                icon={item.icon}
                active={item.subRoutes.some(
                  (sub) => location.pathname === sub.route
                )}
                collapsed={props.collapsed}
              />
            </div>
          </Tooltip>
        );
      } else {
        return (
          <React.Fragment key={index}>
            <div
              className={`sidebar__item-inner expandable ${
                open[index] ? "open" : ""
              }`}
              onClick={() => handleToggle(index)}
            >
              {item.icon && <i>{item.icon}</i>}
              <span>{item.display_name}</span>
              {open[index] ? <ChevronUp /> : <ChevronDown />}
            </div>
            {open[index] && (
              <div className="sidebar__sublist">
                {item.subRoutes.map((subItem, subIndex) => (
                  <div
                    key={subIndex}
                    onClick={() => handleMenuItemClick(subItem.route)}
                  >
                    <SidebarItem
                      title={subItem.display_name}
                      icon={subItem.icon}
                      active={location.pathname === subItem.route}
                      className={classes.nested}
                      collapsed={false}
                    />
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        );
      }
    } else {
      if (props.collapsed) {
        return (
          <Tooltip
            key={index}
            title={item.display_name}
            placement="right"
            arrow
          >
            <div onClick={() => handleMenuItemClick(item.route)}>
              <SidebarItem
                title=""
                icon={item.icon}
                active={index === activeItem}
                collapsed={props.collapsed}
              />
            </div>
          </Tooltip>
        );
      } else {
        return (
          <div key={index} onClick={() => handleMenuItemClick(item.route)}>
            <SidebarItem
              title={item.display_name}
              icon={item.icon}
              active={index === activeItem}
              collapsed={false}
            />
          </div>
        );
      }
    }
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="left"
      open={props.open}
      classes={{
        paperAnchorDockedLeft: classes.borderR,
        paper: classes.drawerPaper,
      }}
    >
      <div className={`sidebar ${props.collapsed ? "sidebar--collapsed" : ""}`}>
        <div className="sidebar__logo">
          {props.collapsed ? (
            <Tooltip title="Hule Stock" placement="right" arrow>
              <div className="sidebar__logo-icon">
                {profilePicture() && profilePicture().trim() !== "" ? (
                  <img
                    src={`${Image_API}/${profilePicture()}`}
                    alt="company logo"
                  />
                ) : (
                  <img src={logo} alt="default company logo" />
                )}
              </div>
            </Tooltip>
          ) : (
            <>
              {profilePicture() && profilePicture().trim() !== "" ? (
                <img
                  src={`${Image_API}/${profilePicture()}`}
                  alt="company logo"
                />
              ) : (
                <img src={logo} alt="default company logo" />
              )}
              <p className="sidebar__logo-text">Hule Stock</p>
            </>
          )}
        </div>

        <div className="sidebar__items">
          {routes.map((item, index) => renderMenuItem(item, index))}
        </div>
      </div>
    </Drawer>
  );
};

export default Sidebar;
